import React, { useState, useCallback, useEffect } from 'react';
import {
  X,
  Search,
  Check,
  Terminal,
  Database,
  Table,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  AlertCircle,
  Box,
  Folder,
  FolderOpen,
  Server,
  ChevronsDown,
  ChevronsUp,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { datasetApi } from '../../api/datasetApi';
import { udfApi } from '../../api/udfApi';

const TreeNode = ({ item, level = 0, onToggle, onSelect, children, isExpanded, icon: Icon, iconColor, isSelected, isDisabled }) => {
  const isLeaf = !children;
  const paddingLeft = level === 0 ? '0' : level === 1 ? '2rem' : level === 2 ? '4rem' : '6rem';

  return (
    <div className="select-none">
      <div
        className={cn(
          "flex items-center gap-2 py-1.5 px-3 rounded-lg transition-all cursor-pointer group border-b border-gray-50/50 dark:border-gray-800/30",
          isExpanded && level === 0 ? "bg-gray-50/20 dark:bg-gray-950/20 hover:bg-gray-100/30 dark:hover:bg-gray-900/30" : "hover:bg-gray-50/20 dark:hover:bg-gray-800/10",
          isSelected && "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800",
          isDisabled && "opacity-50 cursor-not-allowed"
        )}
        style={{ paddingLeft }}
        onClick={() => {
          if (isDisabled) return;
          if (isLeaf) {
            onSelect && onSelect(item);
          } else {
            onToggle();
          }
        }}
      >
        <div className="w-3 flex items-center justify-center shrink-0">
          {!isLeaf ? (
            isExpanded ? <ChevronDown size={level === 0 ? 12 : 11} className="text-gray-400" /> : <ChevronRight size={level === 0 ? 12 : 11} className="text-gray-400" />
          ) : null}
        </div>

        <div className={cn(
          "shrink-0 transition-colors",
          isSelected && isLeaf ? "text-emerald-600" : iconColor || "text-gray-400"
        )}>
          {isSelected && isLeaf ? (
            <Check size={11} strokeWidth={3} />
          ) : Icon ? (
            isLeaf ? (
              <Icon size={11} />
            ) : (
              // For non-leaf nodes, use Folder/FolderOpen by default, but use provided icon if it's not Folder
              Icon === Folder || Icon === Server ? (
                isExpanded ? (
                  Icon === Server ? (
                    <Server size={level === 0 ? 13 : 12} className={iconColor} />
                  ) : (
                    <FolderOpen size={level === 0 ? 13 : 12} className={iconColor} />
                  )
                ) : (
                  <Icon size={level === 0 ? 13 : 12} className={iconColor} />
                )
              ) : (
                isExpanded ? (
                  <FolderOpen size={level === 0 ? 13 : 12} className={iconColor} />
                ) : (
                  <Folder size={level === 0 ? 13 : 12} className={iconColor} />
                )
              )
            )
          ) : (
            <Box size={11} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <span className={cn(
            "font-black uppercase tracking-wider truncate block transition-colors",
            level === 0 ? "text-[10px] text-gray-700 dark:text-gray-300" : "text-[11px] text-gray-900 dark:text-white"
          )}>
            {item.name || item.id}
          </span>
          {item.signature && (
            <div className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block mt-0.5 opacity-60">
              {item.signature}
            </div>
          )}
          {item.type && !item.signature && level > 0 && (
            <div className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block mt-0.5 opacity-60">
              {item.type}
            </div>
          )}
          {item.status && item.status !== 'ready' && (
            <span className={cn(
              "text-[7px] font-black uppercase tracking-widest px-1 py-0.5 rounded-full border shrink-0 inline-block mt-0.5",
              item.status === 'failed' ? "bg-red-50 text-red-500 border-red-100" :
                "bg-amber-50 text-amber-500 border-amber-100 animate-pulse"
            )}>
              {item.status === 'loading' ? 'Loading' :
                item.status === 'generating' ? 'Generating' :
                  item.status}
            </span>
          )}
        </div>
        {level > 0 && (
          <span className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest shrink-0">
            {level === 1 && item.type}
          </span>
        )}
      </div>

      {isExpanded && children}
    </div>
  );
};

const AddItemModal = ({ isOpen, onClose, onSelect, currentItemIds = [], type = 'dataset' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [allItems, setAllItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [itemsTree, setItemsTree] = useState({ manteo: [], bigquery: {}, custom: [] });
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const isDataset = type === 'dataset';
  const Icon = isDataset ? Database : Terminal;
  const title = isDataset ? 'Add Dataset' : 'Add Function';
  const subtitle = isDataset ? 'Select from imported datasets' : 'Select from imported functions';
  const placeholder = isDataset ? 'Search datasets...' : 'Search functions...';
  const emptyMessage = isDataset ? 'No datasets found' : 'No functions found';
  const emptySubmessage = isDataset
    ? 'Import datasets first to add them to your workspace'
    : 'Import functions first to add them to your workspace';

  const loadItems = useCallback(async () => {
    if (!isOpen) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = isDataset ? await datasetApi.getDatasets() : await udfApi.listUDFs();
      setAllItems(data);
    } catch (err) {
      console.error(`Failed to load ${type}s:`, err);
      setError(`Failed to load ${type}s from your library.`);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, isDataset, type]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    // Reset selection when modal opens/closes
    if (!isOpen) {
      setSelectedItemIds(new Set());
    }
  }, [isOpen]);

  // Organize items into tree structure
  useEffect(() => {
    const tree = {
      manteo: [],
      bigquery: {}, // project -> dataset -> [items]
      custom: []
    };

    const itemsList = Array.isArray(allItems) ? allItems : [];

    if (isDataset) {
      // Dataset tree structure
      itemsList.forEach(d => {
        if (d.type === 'upload' || d.type === 'parquet' || d.type === 'csv' || d.type === 'sample') {
          tree.manteo.push(d);
        } else if (d.type === 'bigquery') {
          const parts = d.id.split('.');
          if (parts.length === 3) {
            const [project, dataset, table] = parts;
            if (!tree.bigquery[project]) {
              tree.bigquery[project] = {};
            }
            if (!tree.bigquery[project][dataset]) {
              tree.bigquery[project][dataset] = [];
            }
            tree.bigquery[project][dataset].push({ ...d, tableName: table });
          } else if (parts.length === 2) {
            const [dataset, table] = parts;
            const project = 'Current Project';
            if (!tree.bigquery[project]) tree.bigquery[project] = {};
            if (!tree.bigquery[project][dataset]) tree.bigquery[project][dataset] = [];
            tree.bigquery[project][dataset].push({ ...d, tableName: table });
          } else {
            const fallbackProject = 'Direct Connections';
            const fallbackDataset = 'Generic';
            if (!tree.bigquery[fallbackProject]) tree.bigquery[fallbackProject] = {};
            if (!tree.bigquery[fallbackProject][fallbackDataset]) tree.bigquery[fallbackProject][fallbackDataset] = [];
            tree.bigquery[fallbackProject][fallbackDataset].push({ ...d, tableName: d.name || d.id });
          }
        }
      });
    } else {
      // Function tree structure
      itemsList.forEach(func => {
        if (func.source === 'bigquery' && func.routine_id) {
          const parts = func.routine_id.split('.');
          if (parts.length >= 3) {
            const project = parts[0];
            const dataset = parts[1];
            const functionName = parts.slice(2).join('.');

            if (!tree.bigquery[project]) {
              tree.bigquery[project] = {};
            }
            if (!tree.bigquery[project][dataset]) {
              tree.bigquery[project][dataset] = [];
            }
            tree.bigquery[project][dataset].push({
              ...func,
              project,
              dataset,
              functionName
            });
          }
        } else if (func.source === 'custom') {
          tree.custom.push(func);
        }
      });
    }

    setItemsTree(tree);

    // Expand all nodes by default
    const allNodeIds = new Set();
    if (tree.manteo.length > 0) allNodeIds.add('manteo');
    if (Object.keys(tree.bigquery).length > 0) allNodeIds.add('bigquery');
    if (tree.custom.length > 0) allNodeIds.add('custom');
    Object.keys(tree.bigquery).forEach(project => {
      allNodeIds.add(project);
      Object.keys(tree.bigquery[project]).forEach(dataset => {
        allNodeIds.add(dataset);
      });
    });
    setExpandedNodes(allNodeIds);
  }, [allItems, isDataset]);

  const toggleNode = (nodeId) => {
    const next = new Set(expandedNodes);
    next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
    setExpandedNodes(next);
  };

  const expandAll = () => {
    const allNodeIds = new Set();
    if (itemsTree.manteo.length > 0) allNodeIds.add('manteo');
    if (Object.keys(itemsTree.bigquery).length > 0) allNodeIds.add('bigquery');
    if (itemsTree.custom.length > 0) allNodeIds.add('custom');
    Object.keys(itemsTree.bigquery).forEach(project => {
      allNodeIds.add(project);
      Object.keys(itemsTree.bigquery[project]).forEach(dataset => {
        allNodeIds.add(dataset);
      });
    });
    setExpandedNodes(allNodeIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const toggleSelection = (item) => {
    if (currentItemIds.includes(item.id)) return;

    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      return next;
    });
  };

  const handleAddSelected = async () => {
    const selectedItems = allItems.filter(item => selectedItemIds.has(item.id));
    if (selectedItems.length > 0) {
      if (isDataset) {
        // Add datasets as a batch (like functions)
        onSelect('library', selectedItems);
      } else {
        // Add functions as a batch
        onSelect('udf-library', selectedItems);
      }
      setSelectedItemIds(new Set());
      onClose();
    }
  };

  // Filter tree based on search query
  const filteredTree = {
    manteo: itemsTree.manteo.filter(item =>
      (item.name || item.id || "").toLowerCase().includes(searchQuery.toLowerCase())
    ),
    bigquery: Object.fromEntries(
      Object.entries(itemsTree.bigquery).map(([projectId, datasets]) => {
        const filteredDatasets = Object.fromEntries(
          Object.entries(datasets).map(([datasetId, items]) => [
            datasetId,
            items.filter(item => {
              const q = searchQuery.toLowerCase();
              if (isDataset) {
                return (item.tableName || item.name || item.id || "").toLowerCase().includes(q);
              } else {
                return (item.functionName || item.name || "").toLowerCase().includes(q) ||
                       (item.signature || "").toLowerCase().includes(q) ||
                       (item.description || "").toLowerCase().includes(q);
              }
            })
          ]).filter(([_, items]) => items.length > 0)
        );
        return [projectId, filteredDatasets];
      }).filter(([_, datasets]) => Object.keys(datasets).length > 0)
    ),
    custom: itemsTree.custom.filter(item => {
      const q = searchQuery.toLowerCase();
      return (item.name || "").toLowerCase().includes(q) ||
             (item.signature || "").toLowerCase().includes(q) ||
             (item.description || "").toLowerCase().includes(q);
    })
  };

  const hasBigQuery = Object.keys(filteredTree.bigquery).length > 0;
  const hasManteo = filteredTree.manteo.length > 0;
  const hasCustom = filteredTree.custom.length > 0;
  const hasAnyItems = hasManteo || hasBigQuery || hasCustom;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-gray-950 w-full max-w-2xl rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="p-8 border-b border-gray-50 dark:border-gray-900/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-2xl">
              <Icon size={24} />
            </div>
            <div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter leading-none">
                {title}
              </h3>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                {subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadItems}
              disabled={isLoading}
              className="p-2 text-gray-400 hover:text-emerald-600 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={18} className={cn(isLoading && "animate-spin")} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search and Expand/Collapse */}
        <div className="p-6 border-b border-gray-50 dark:border-gray-900/20 shrink-0 space-y-3">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
            />
          </div>
          {hasAnyItems && (
            <div className="flex items-center gap-2">
              <button
                onClick={expandAll}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 transition-colors"
              >
                <ChevronsDown size={14} />
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 transition-colors"
              >
                <ChevronsUp size={14} />
                Collapse All
              </button>
            </div>
          )}
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 shrink-0">
            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest">Error</p>
              <p className="text-xs font-bold text-red-500 dark:text-red-300 mt-1">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded-lg transition-colors">
              <X size={14} className="text-red-400" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar min-h-[300px]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading {type}s...</p>
            </div>
          ) : !hasAnyItems ? (
            <div className="flex flex-col items-center justify-center py-20 bg-gray-50 dark:bg-gray-900/50 rounded-[40px] border-2 border-dashed border-gray-100 dark:border-gray-800">
              <Icon size={40} className="text-gray-200 dark:text-gray-800 mb-4" />
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{emptyMessage}</p>
              <p className="text-[9px] text-gray-400 font-medium mt-2">{emptySubmessage}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Manteo Warehouse Section (datasets only) */}
              {isDataset && hasManteo && (
                <TreeNode
                  item={{ id: 'manteo', name: 'Manteo Warehouse', type: 'Internal High-Performance Storage' }}
                  level={0}
                  isExpanded={expandedNodes.has('manteo')}
                  onToggle={() => toggleNode('manteo')}
                  icon={Folder}
                  iconColor="text-emerald-500"
                  children={
                    <div className="mt-1">
                      {filteredTree.manteo.map(item => {
                        const isAlreadyAdded = currentItemIds.includes(item.id);
                        const isSelected = selectedItemIds.has(item.id);
                        return (
                          <TreeNode
                            key={item.id}
                            item={{ id: item.id, name: item.name || item.id, type: item.type, status: item.status }}
                            level={1}
                            onSelect={() => !isAlreadyAdded && toggleSelection(item)}
                            icon={Table}
                            iconColor="text-emerald-500/70"
                            isSelected={isSelected}
                            isDisabled={isAlreadyAdded || (item.status && item.status !== 'ready')}
                          />
                        );
                      })}
                    </div>
                  }
                />
              )}

                  {/* BigQuery Section */}
                  {hasBigQuery && (
                    <TreeNode
                      item={{
                        id: 'bigquery',
                        name: isDataset ? 'BigQuery Connection' : 'BigQuery Connection',
                        type: 'Enterprise Data Warehouse'
                      }}
                      level={0}
                      isExpanded={expandedNodes.has('bigquery')}
                      onToggle={() => toggleNode('bigquery')}
                      icon={Server}
                      iconColor="text-blue-500"
                      children={
                        <div className="mt-1">
                          {Object.entries(filteredTree.bigquery).map(([projectId, datasets]) => (
                            <TreeNode
                              key={projectId}
                              item={{ id: projectId, name: projectId, type: 'GCP Project' }}
                              level={1}
                              isExpanded={expandedNodes.has(projectId)}
                              onToggle={() => toggleNode(projectId)}
                              icon={Folder}
                              iconColor="text-blue-500"
                              children={
                                <div className="mt-1">
                                  {Object.entries(datasets).map(([datasetId, items]) => (
                                    <TreeNode
                                      key={datasetId}
                                      item={{ id: datasetId, name: datasetId, type: 'Dataset' }}
                                      level={2}
                                      isExpanded={expandedNodes.has(datasetId)}
                                      onToggle={() => toggleNode(datasetId)}
                                      icon={Folder}
                                      iconColor="text-blue-400"
                                  children={
                                    <div className="mt-1">
                                      {items.map(item => {
                                        const isAlreadyAdded = currentItemIds.includes(item.id);
                                        const isSelected = selectedItemIds.has(item.id);
                                        const itemName = isDataset
                                          ? (item.tableName || item.name)
                                          : (item.functionName || item.name);
                                        return (
                                          <TreeNode
                                            key={item.id}
                                            item={{
                                              id: item.id,
                                              name: itemName,
                                              type: isDataset ? 'Table' : undefined,
                                              signature: item.signature,
                                              status: item.status
                                            }}
                                            level={3}
                                            onSelect={() => !isAlreadyAdded && toggleSelection(item)}
                                            icon={isDataset ? Table : Terminal}
                                            iconColor={isDataset ? "text-blue-500/50" : "text-blue-500/50"}
                                            isSelected={isSelected}
                                            isDisabled={isAlreadyAdded || (item.status && item.status !== 'ready')}
                                          />
                                        );
                                      })}
                                    </div>
                                  }
                                />
                              ))}
                            </div>
                          }
                        />
                      ))}
                    </div>
                  }
                />
              )}

              {/* Custom Functions Section (functions only) */}
              {!isDataset && hasCustom && (
                <TreeNode
                  item={{ id: 'custom', name: 'Custom Functions', type: 'User-Defined Functions' }}
                  level={0}
                  isExpanded={expandedNodes.has('custom')}
                  onToggle={() => toggleNode('custom')}
                  icon={Folder}
                  iconColor="text-emerald-500"
                  children={
                    <div className="mt-1">
                      {filteredTree.custom.map(item => {
                        const isAlreadyAdded = currentItemIds.includes(item.id);
                        const isSelected = selectedItemIds.has(item.id);
                        return (
                          <TreeNode
                            key={item.id}
                            item={{
                              id: item.id,
                              name: item.name,
                              signature: item.signature
                            }}
                            level={1}
                            onSelect={() => !isAlreadyAdded && toggleSelection(item)}
                            icon={Terminal}
                            iconColor="text-emerald-500/70"
                            isSelected={isSelected}
                            isDisabled={isAlreadyAdded}
                          />
                        );
                      })}
                    </div>
                  }
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-50 dark:border-gray-900/50 bg-gray-50/30 dark:bg-gray-900/30 shrink-0">
          <div className="flex items-center justify-between gap-4">
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
              {selectedItemIds.size > 0 ? (
                <span>{selectedItemIds.size} {type}{selectedItemIds.size !== 1 ? 's' : ''} selected</span>
              ) : (
                <span>Import {type}s from <span className="text-emerald-600">Data Sources</span> to add them here</span>
              )}
            </p>
            {selectedItemIds.size > 0 && (
              <button
                onClick={handleAddSelected}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-semibold uppercase tracking-wider transition-all"
              >
                Add Selected ({selectedItemIds.size})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddItemModal;
